import { apiClient, isScholarshipConfirmResult } from '@app/lib'
import { useSugarContext } from '@app/providers'
import {
  type SugarEnrollmentsResponse,
  type SugarScholarshipConfirmInput,
  type SugarScholarshipConfirmResponse
} from '@app/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { map } from 'lodash'

export const useConfirmScholarship = () => {
  const queryClient = useQueryClient()
  const { rosterProgramYearId: programYearId, siteId } = useSugarContext()

  const { isPending, mutateAsync } = useMutation({
    mutationFn: async (input: SugarScholarshipConfirmInput): Promise<SugarScholarshipConfirmResponse> => {
      const { data } = await apiClient.post<SugarScholarshipConfirmResponse>('/api/scholarship/confirm', input)

      // Returned whole rather than unwrapped to data.data, matching every other
      // mutation hook here. Business rejections arrive as HTTP 200 with
      // success: false -- the lock message, a terminal status, a blocking
      // CCCAP/Head Start stream -- so axios never throws for them, and the
      // caller needs `messages` to show the provider what the server said.
      // Only a transport failure rejects, and the caller's catch covers that.
      return data
    },
    // React Query's MutationObserver calls setOptions() on every re-render
    // while a mutation is pending, which replaces the in-flight mutation's
    // onSuccess with the closure from the latest render. If the provider
    // switches site or program year while the confirm POST is still in
    // flight, an onSuccess reading programYearId/siteId ambiently would patch
    // the NEW roster's cache entry instead of the one the confirmation was
    // actually issued against -- a silent no-op on screen. onMutate fires
    // synchronously at the start of mutate(), before any re-render can swap
    // the callbacks, so it captures the key that was current when the
    // provider clicked Confirm.
    onMutate: () => ({ programYearId, siteId }),
    onSuccess: (response, input, context) => {
      // A rejected confirm resolves now rather than throwing, so onSuccess no
      // longer means the confirm succeeded and the body is the only authority.
      // Without this guard a rejection would patch the row from a null result
      // and flip it to Active against a server that refused.
      if (!response.success || !isScholarshipConfirmResult(response.data)) {
        return
      }

      const result = response.data

      // Patched, never invalidated. The roster is form-bound: invalidating
      // refetches it and can reseed the form, which drops per-row save errors
      // and abandons a batch save that is still in flight. Exactly one
      // enrollment changed, so exactly one is rewritten.
      //
      // The shape here has to match what useEnrollments puts in the cache. It
      // holds a flat SugarEnrollmentsResponse now that the roster arrives whole;
      // patching InfiniteData.pages against it was a silent no-op, because
      // `old.pages` is undefined and lodash map returns [] for it -- the write
      // succeeded, `data` went untouched, and the row never flipped to Active.
      // The generic on setQueryData is an assertion, not a check, so nothing
      // caught it at compile time.
      queryClient.setQueryData<SugarEnrollmentsResponse>(
        ['enrollments', { programYearId: context.programYearId, siteId: context.siteId }],
        (old) =>
          old && {
            ...old,
            data: map(old.data, (item) =>
              item.enrollment.id === input.enrollmentId
                ? {
                    ...item,
                    enrollment: {
                      ...item.enrollment,
                      gapStatus: result.status,
                      gapAwardAmount: result.awardAmount,
                      // The disabling of all four funding selects derives from
                      // gapStatus at render (isScholarshipLocked,
                      // funding.ts), which this patch already writes, so
                      // the lock needs nothing extra here (MIQ-2745, extended to
                      // UPK and SFP by MIQ-2702). This write IS the lock: the
                      // roster reads it straight out of this cache entry rather
                      // than from a flag copied onto the form row.
                      // The tri-state below serves its remaining consumers --
                      // AttendanceTable's DPP Scholarship label and pre-2026/27
                      // rules, and EnrollmentViewModal -- and only an 'active' result
                      // changes it: formatGapStatus collapses under_dpp_review
                      // to the 'Pending' the row already carries.
                      ...(result.status === 'active' ? { gapScholarshipStatus: 'Yes' as const } : null)
                    }
                  }
                : item
            )
          }
      )
    }
  })

  return {
    confirmScholarship: mutateAsync,
    isConfirmingScholarship: isPending
  }
}
